/**
 * Session idle timing (configure in `.env` / `.env.local`).
 *
 * - REACT_APP_SESSION_MAX_IDLE_MS — invalidate session after this much inactivity
 * - REACT_APP_SESSION_WARN_BEFORE_MS — show warning modal this long before max idle
 *   (popup appears at max − warn, e.g. 30 s max and 10 s warn → popup at 20 s)
 *
 * Restart `npm start` after changing env files.
 */

const DEFAULT_MAX_IDLE_MS = 30 * 60 * 1000;
const DEFAULT_WARN_BEFORE_MS = 5 * 60 * 1000;

function parsePositiveMs(raw: string | undefined, fallback: number): number {
  if (!raw?.trim()) {
    return fallback;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/** Max inactive time (ms) before the session is invalidated. */
export const SESSION_MAX_IDLE_MS = parsePositiveMs(
  process.env.REACT_APP_SESSION_MAX_IDLE_MS ??
    process.env.REACT_APP_SESSION_IDLE_MS,
  DEFAULT_MAX_IDLE_MS,
);

/** Warning window length (ms) before max idle — popup shows at (max − warn). */
export const SESSION_WARN_BEFORE_MS = parsePositiveMs(
  process.env.REACT_APP_SESSION_WARN_BEFORE_MS,
  DEFAULT_WARN_BEFORE_MS,
);

const warnBeforeMs = Math.min(SESSION_WARN_BEFORE_MS, SESSION_MAX_IDLE_MS - 1);

/** Idle duration (ms) at which the warning modal is first shown. */
export const SESSION_WARNING_AT_IDLE_MS = Math.max(
  0,
  SESSION_MAX_IDLE_MS - warnBeforeMs,
);

const isShortSessionTest = SESSION_MAX_IDLE_MS < 60_000;

export const SESSION_ACTIVITY_THROTTLE_MS = isShortSessionTest ? 500 : 2_000;

export const SESSION_TICK_INTERVAL_MS = isShortSessionTest ? 250 : 1_000;
